// @ts-check
// Desativar, excluir e mudar a coleção de um ou mais fluxos (spec 2.0.0, fluxos.md seções 20.1 e 20.12; contrato 09 de
// fluxos, seção 17). Usados pela lista (linha e seleção em lote), pelo editor e pelas estatísticas. Cada um confirma,
// chama a API fluxo a fluxo e devolve o que mudou; um erro num fluxo não impede os outros.
import { html } from 'htm/preact';
import { useState } from 'preact/hooks';
import { deactivateFlow, deleteFlow, updateFlowDetails } from '../../data/flows.js';
import { Button } from '../../ui/button.js';
import { showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { formatNumber } from '../../lib/format.js';
import { MAXIMUM_FLOW_COLLECTION_LENGTH } from '../../../../shared/flows/flow-limits.js';
import { FlowDialog } from './flow-dialog.js';
import { FlowField } from './flow-ui.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {{ id: string, name: string, status: string, inFlow?: number | null }} FlowTarget
 * @typedef {import('../../data/flows.js').FlowDetail} FlowDetail
 */

/**
 * Opção de rádio com título e explicação.
 * @param {{ name: string, value: string, checked: boolean, title: string, description?: string | null, onSelect: (value: string) => void, disabled?: boolean }} props
 */
export function FlowRadioOption({ name, value, checked, title, description = null, onSelect, disabled = false }) {
  return html`<label class="flow-radio-option" data-checked=${checked ? 'true' : undefined} data-disabled=${disabled ? 'true' : undefined}>
    <input type="radio" name=${name} value=${value} checked=${checked} disabled=${disabled} onChange=${() => onSelect(value)} />
    <span class="flow-radio-texts">
      <span class="flow-radio-title">${title}</span>
      ${description ? html`<span class="flow-radio-description">${description}</span>` : null}
    </span>
  </label>`;
}

/**
 * Executa uma ação em cada fluxo, avisa os erros e diz quantos deram certo.
 * @template TResult
 * @param {FlowTarget[]} targets @param {(target: FlowTarget) => Promise<TResult>} action
 * @returns {Promise<Array<{ target: FlowTarget, result: TResult }>>}
 */
async function runForEach(targets, action) {
  /** @type {Array<{ target: FlowTarget, result: any }>} */
  const done = [];
  for (const target of targets) {
    try {
      done.push({ target, result: await action(target) });
    } catch (error) {
      showErrorSnackbar(error);
    }
  }
  return done;
}

/**
 * "Desativar fluxo": encerrar o percurso de quem está no fluxo (recomendado) ou manter os leads parados.
 * @param {{ flows: FlowTarget[], onDone: (updated: FlowDetail[]) => void, onClose: () => void }} props
 */
export function FlowDeactivateDialog({ flows, onDone, onClose }) {
  const [mode, setMode] = useState(/** @type {'STOP_RUNS' | 'HOLD_RUNS'} */ ('STOP_RUNS'));
  const [isBusy, setIsBusy] = useState(false);
  const targets = flows.filter((flow) => flow.status === 'ACTIVE');
  const inFlow = targets.reduce((total, flow) => total + Number(flow.inFlow ?? 0), 0);
  const confirm = async () => {
    setIsBusy(true);
    const done = await runForEach(targets, (target) => deactivateFlow(target.id, mode));
    setIsBusy(false);
    if (done.length > 0) showSuccessSnackbar(t('flows.lifecycle.deactivated', { count: done.length }));
    onDone(done.map((item) => item.result.flow));
  };
  return html`<${FlowDialog} title=${targets.length > 1 ? t('flows.lifecycle.deactivateManyTitle', { count: targets.length }) : t('flows.lifecycle.deactivateTitle')}
    onClose=${onClose} testId="flow-deactivate-dialog" role="alertdialog"
    footer=${html`<${Button} title=${t('common.cancel')} variant="secondary" size="small" onClick=${onClose} />
      <${Button} title=${t('flows.lifecycle.deactivate')} accent="danger" size="small" isLoading=${isBusy} disabled=${targets.length === 0} onClick=${confirm} />`}>
    <p class="flow-dialog-text">${t('flows.lifecycle.inFlowNow', { count: inFlow })}</p>
    <div class="flow-radio-group" role="radiogroup" aria-label=${t('flows.lifecycle.deactivateTitle')}>
      <${FlowRadioOption} name="flow-deactivate-mode" value="STOP_RUNS" checked=${mode === 'STOP_RUNS'} title=${t('flows.lifecycle.stopRuns')}
        description=${t('flows.lifecycle.stopRunsHint')} onSelect=${() => setMode('STOP_RUNS')} />
      <${FlowRadioOption} name="flow-deactivate-mode" value="HOLD_RUNS" checked=${mode === 'HOLD_RUNS'} title=${t('flows.lifecycle.holdRuns')}
        description=${t('flows.lifecycle.holdRunsHint')} onSelect=${() => setMode('HOLD_RUNS')} />
    </div>
  <//>`;
}

/**
 * "Excluir fluxo": tira quem está no fluxo e some com as estatísticas; não dá para desfazer.
 * @param {{ flows: FlowTarget[], onDone: (deletedIds: string[]) => void, onClose: () => void }} props
 */
export function FlowDeleteDialog({ flows, onDone, onClose }) {
  const [isBusy, setIsBusy] = useState(false);
  const inFlow = flows.reduce((total, flow) => total + Number(flow.inFlow ?? 0), 0);
  const confirm = async () => {
    setIsBusy(true);
    const done = await runForEach(flows, (target) => deleteFlow(target.id));
    setIsBusy(false);
    if (done.length > 0) showSuccessSnackbar(t('flows.lifecycle.deleted', { count: done.length }));
    onDone(done.map((item) => item.target.id));
  };
  return html`<${FlowDialog} title=${flows.length > 1 ? t('flows.lifecycle.deleteManyTitle', { count: flows.length }) : t('flows.lifecycle.deleteTitle')}
    onClose=${onClose} testId="flow-delete-dialog" role="alertdialog"
    footer=${html`<${Button} title=${t('common.cancel')} variant="secondary" size="small" onClick=${onClose} />
      <${Button} title=${t('flows.lifecycle.delete')} accent="danger" size="small" isLoading=${isBusy} onClick=${confirm} />`}>
    ${flows.length === 1 ? html`<p class="flow-dialog-text"><strong>${flows[0].name}</strong></p>` : null}
    <p class="flow-dialog-text">${inFlow > 0 ? t('flows.lifecycle.deleteWithRuns', { count: inFlow }) : t('flows.lifecycle.deleteText')}</p>
  <//>`;
}

/**
 * "Coleção...": escolher uma coleção (ou nenhuma) para um ou mais fluxos.
 * @param {{ flows: FlowTarget[], collections: string[], initialCollection?: string | null, onDone: (updated: FlowDetail[]) => void, onClose: () => void }} props
 */
export function FlowCollectionDialog({ flows, collections, initialCollection = null, onDone, onClose }) {
  const [value, setValue] = useState(initialCollection ?? '');
  const [isBusy, setIsBusy] = useState(false);
  const confirm = async () => {
    setIsBusy(true);
    const collection = value.trim() ? value.trim().slice(0, MAXIMUM_FLOW_COLLECTION_LENGTH) : null;
    const done = await runForEach(flows, (target) => updateFlowDetails(target.id, { collection }));
    setIsBusy(false);
    if (done.length > 0) showSuccessSnackbar(t('flows.lifecycle.collectionSaved', { count: done.length }));
    onDone(done.map((item) => item.result));
  };
  return html`<${FlowDialog} title=${t('flows.lifecycle.collectionTitle')} onClose=${onClose} testId="flow-collection-dialog" onSubmit=${confirm}
    footer=${html`<${Button} title=${t('common.cancel')} variant="secondary" size="small" onClick=${onClose} />
      <${Button} title=${t('flows.lifecycle.collectionSave')} accent="blue" size="small" type="submit" isLoading=${isBusy} />`}>
    <p class="flow-dialog-text">${flows.length > 1 ? t('flows.lifecycle.collectionManyText', { count: flows.length }) : t('flows.lifecycle.collectionText')}</p>
    <${FlowField} label=${t('flows.nameDialog.collection')} htmlFor="flow-collection-move" hint=${t('flows.lifecycle.collectionHint')}>
      <input id="flow-collection-move" class="text-input" value=${value} maxLength=${MAXIMUM_FLOW_COLLECTION_LENGTH} list="flow-collection-move-options" data-autofocus="true"
        onInput=${(/** @type {InputEvent} */ event) => setValue(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
      <datalist id="flow-collection-move-options">${collections.map((collection) => html`<option key=${collection} value=${collection} />`)}</datalist>
    <//>
  <//>`;
}
